import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Cross, Lock, LockOpen, Pencil, Plus, Users } from '@saleshandy/icons';
import { Button, ImageIcon } from '@saleshandy/design-system';
import { Modal } from 'react-bootstrap';
import { useHistory } from 'react-router';
import { useOverlayScrollbars } from 'overlayscrollbars-react';
import 'overlayscrollbars/styles/overlayscrollbars.css';

import toaster from '../../../../shared/toaster';

import type { IProps } from './prospect-filters-modal-container';
import {
  FilterGroup,
  FilterLogic,
  FilterOperation,
  ProspectFilterField,
} from './types';

import ProspectFiltersSelectDropdown from './components/prospect-filters-select-dropdown';
import ProspectFiltersConditionLogic from './components/prospect-filters-condition-logic';
import ProspectFiltersOperations from './components/prospect-filters-operations';
import ProspectFiltersValueComponent from './components/prospect-filters-value-component';
import ProspectFiltersSaveAsAViewModal from './components/prospect-filters-save-as-a-view-modal';

import {
  executeOnRequestStatus,
  executeOnErrorWithErrorCheck,
  getIsRequestPending,
  truncate,
  SubscriptionPlans,
} from '../../../../shared/utils';
import { getDefaultFilterGroupObject } from './utils/get-default-filter-group-object';
import { accessibleOnClick } from '../../../../shared/utils/accessible-on-click';
import { formatFilterGroupsForPayload } from './utils/format-filter-group-for-payload';
import { doesProspectFilterHasErrors } from './utils/does-prospect-filters-has-errors';
import { constructFilterGroupFromPayload } from './utils/construct-filter-group-from-payload';
import {
  getIsFieldsDoesNotExist,
  getProspectFiledLabel,
  isAddFilterButtonEnabled,
} from './utils/helpers';
import { AnalyticsEvents } from '../../../../shared/enums/analytics';
import {
  getUserPlanName,
  getUserRole,
  getUserShAccountId,
  getUserTrackingId,
} from '../../../../shared/utils/user-details';
import { Images } from '../../../../shared/app-constants';
import { OverlayTooltip } from '../../../../shared/design-system/components/overlay';
import Input from '../../../../shared/design-system/components/input';
import Select from '../../../../shared/design-system/components/select/select';
import { Placement } from '../../../../shared/design-system/components/overlay-taget/enums';
import Transfer from '../../../../shared/design-system/components/transfer/transfer';
import TruncatedTextWithAutoTooltip from '../../../../shared/components/truncated-text-with-auto-tooltip';
import hasPermission from '../../../../shared/utils/access-control/has-permission';
import { Permissions } from '../../../../shared/utils/access-control/enums/permissions';
import { ViewType } from '../../enums/prospect';

const MAX_FILTERS_CONDITION = 30;

const ProspectFiltersModal: React.FC<IProps> = ({
  show,
  onClose,

  // Auto Run Condition
  onApplyAiColEnrichmentAutoRunSettingsCondition = null,
  headerTitle = null,
  autoRunConditionFilterGroups,
  autoRunConditionFilterGroupsLogic,

  isCreateViewModal = false,

  filters,
  setProspectFilters,
  fields,

  selectedSavedView,
  sendGetContactListByViewIdRequest,
  sendGetProspectListCountRequest,
  removedSelectedSavedView,

  prospectFiltersFieldsList,
  getProspectFiltersFieldsListRequestStatus,
  getProspectFiltersFieldsListRequestError,
  sendGetProspectFiltersFieldsListRequest,
  resetGetProspectFiltersFieldsListRequest,

  teamsList,
  getTeamsListRequestStatus,
  sendGetTeamsListRequest,

  saveProspectFiltersViewRequestStatus,
  saveProspectFiltersViewRequestError,
  sendSaveProspectFiltersViewRequest,
  resetSaveProspectFiltersViewRequest,

  updateProspectFiltersViewRequestStatus,
  updateProspectFiltersViewRequestError,
  sendUpdateProspectFiltersViewRequest,
  resetUpdateProspectFiltersViewRequest,
  getFilterViewListRequest,
  currentViewType,
  setShouldFetchInitialList,
  prospectViewIdFilter,
  setProspectViewIdFilter,
  subscriptionPlan,
}) => {
  const history = useHistory();
  const canViewTeamOption =
    hasPermission(Permissions.ACCOUNT_TEAM_MEMBER_READ) ||
    hasPermission(Permissions.TEAM_MEMBER_READ) ||
    hasPermission(Permissions.TEAM_TEAM_MEMBER_READ);

  const canViewTeamOptionAsPerPlan =
    subscriptionPlan !== SubscriptionPlans.FREE;

  const [prospectFields, setProspectFields] = useState([]);
  const [filterGroups, setFilterGroups] = useState<FilterGroup[]>([
    getDefaultFilterGroupObject(1),
  ]);
  const [groupLogic, setGroupLogic] = useState(FilterLogic.AND);

  const hasErrors = useMemo(
    () => doesProspectFilterHasErrors(filterGroups, fields),
    [filterGroups, fields],
  );

  const initialFilters = useMemo(
    () =>
      JSON.stringify(filterGroups) ===
        JSON.stringify([getDefaultFilterGroupObject(1)]) ||
      JSON.stringify(filterGroups) ===
        JSON.stringify([getDefaultFilterGroupObject(1, FilterLogic.OR)]),

    [filterGroups],
  );

  const isMaxFiltersReached = useMemo(() => {
    // Calculate the total number of conditions across all filter groups
    const totalConditions = filterGroups.reduce(
      (count, group) => count + group.conditions.length,
      0,
    );

    // Check if the total matches the MAX_FILTERS_CONDITION
    return totalConditions === MAX_FILTERS_CONDITION;
  }, [filterGroups]);

  const overlayRef = useRef(null);

  const [initialize] = useOverlayScrollbars({
    options: {
      overflow: {
        x: 'hidden',
      },
      scrollbars: {
        autoHide: 'leave',
        autoHideDelay: 500,
      },
    },
    defer: true,
  });

  const isFiltersDirty = useRef<boolean>(false);

  const [sharedWith, setSharedWith] = useState<string>('private');
  const [shareWithTeameamIds, setShareWithTeamIds] = useState<number[]>([]);
  const [showShareWithDropdown, setShowShareWithDropdown] = useState<boolean>(
    false,
  );
  const [viewNameError, setViewNameError] = useState(false);

  const shareWithOptions = [
    {
      key: 'private',
      label: 'Private (Only me)',
      icon: <Lock className="gray-txt-15" />,
    },
    {
      key: 'public',
      label: 'Public',
      icon: <LockOpen className="gray-txt-15" />,
    },
    canViewTeamOption &&
      canViewTeamOptionAsPerPlan && {
        key: 'team',
        label: 'Teams',
        icon: <Users width={16} height={16} className="gray-txt-15" />,
      },
  ];

  // Save As A View State & Functions
  const [saveAsAViewModal, setSaveAsAViewModal] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false);
  const [editedTitle, setEditedTitle] = useState<string>(headerTitle || '');
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const isSaveViewBtnDisabled = useMemo(
    () =>
      (filterGroups?.length === 1 &&
        filterGroups?.[0]?.conditions?.length === 1 &&
        filterGroups?.[0]?.conditions?.[0]?.field === null) ||
      isSaving,
    [filterGroups, isSaving],
  );

  const showSaveAsAViewModal = () => {
    if (!hasErrors) {
      setSaveAsAViewModal(true);
    }
  };

  const hideSaveAsAViewModal = () => {
    setSaveAsAViewModal(false);
  };

  const onClearAll = () => {
    setFilterGroups([getDefaultFilterGroupObject(1)]);
  };

  const onChangeShareWith = (selectedOptions: any[]) => {
    const option = selectedOptions?.[0];

    if (option && !['private', 'public', 'team'].includes(option?.key)) {
      // numeric team id
      const teamId = parseInt(option.key, 10);
      const isSelected = shareWithTeameamIds.includes(teamId);

      if (isSelected) {
        setShareWithTeamIds(shareWithTeameamIds.filter((id) => id !== teamId));
      } else {
        setShareWithTeamIds([...shareWithTeameamIds, teamId]);
      }
      isFiltersDirty.current = true;
      // Keep dropdown open when selecting team members
    } else if (option) {
      setSharedWith(option.key);
      isFiltersDirty.current = true;

      // **FIXED — load only if empty**
      if (
        option.key === 'team' &&
        teamsList.length === 0 &&
        canViewTeamOption &&
        canViewTeamOptionAsPerPlan
      ) {
        sendGetTeamsListRequest();
      }

      if (option.key !== 'team') {
        setShareWithTeamIds([]);
        // Close dropdown for non-team options
        setShowShareWithDropdown(false);
      }
      // Keep dropdown open when 'team' is selected
    }
  };

  const onSaveAsView = (
    filterName: string,
    isShared: boolean,
    teamIds: number[],
    action: 'save' | 'update',
  ) => {
    if (action === 'update') {
      sendUpdateProspectFiltersViewRequest({
        id: selectedSavedView.id.toString(),
        filterName,
        filterCriteria: formatFilterGroupsForPayload({
          groupLogic,
          filterGroups,
        }),
        isShared,
        teamIds,
      });
    } else {
      sendSaveProspectFiltersViewRequest({
        filterName,
        filterCriteria: formatFilterGroupsForPayload({
          groupLogic,
          filterGroups,
        }),
        isShared,
        teamIds,
      });
    }
  };

  const onSelectProspectFields = (
    option: ProspectFilterField,
    groupIndex: number,
    conditionIndex: number,
  ) => {
    // Create a deep copy of the filterGroups state
    const newFilterGroups = filterGroups.map((group) => ({
      ...group,
      conditions: [...group.conditions],
    }));

    // Update the specific condition in the specific group
    newFilterGroups[groupIndex] = {
      ...newFilterGroups[groupIndex],
      conditions: newFilterGroups[groupIndex].conditions.map((condition, idx) =>
        idx === conditionIndex
          ? {
              ...condition,
              field: option,
              operation: option?.operations?.[0] || null,
              value: undefined,
            }
          : condition,
      ),
    };

    setFilterGroups(newFilterGroups);
  };

  const onSelectProspectFieldsOperation = (
    operation: FilterOperation,
    groupIndex: number,
    conditionIndex: number,
  ) => {
    // Create a deep copy of the filterGroups state
    const newFilterGroups = filterGroups.map((group) => ({
      ...group,
      conditions: [...group.conditions],
    }));

    // Update the specific condition operation in the specific group
    newFilterGroups[groupIndex] = {
      ...newFilterGroups[groupIndex],
      conditions: newFilterGroups[groupIndex].conditions.map((condition, idx) =>
        idx === conditionIndex
          ? { ...condition, operation, value: undefined }
          : condition,
      ),
    };

    setFilterGroups(newFilterGroups);
  };

  const onUpdateFilterGroupLogic = (logic: FilterLogic, groupIndex: number) => {
    // Create a deep copy of the filterGroups state
    const newFilterGroups = filterGroups.map((group) => ({
      ...group,
      conditions: [...group.conditions],
    }));

    // Add the filter logic in the specific group
    newFilterGroups[groupIndex] = {
      ...newFilterGroups[groupIndex],
      logic,
    };

    setFilterGroups(newFilterGroups);
  };

  const onAddFilterCondition = (groupIndex: number) => {
    // Create a deep copy of the filterGroups state
    const newFilterGroups = filterGroups.map((group) => ({
      ...group,
      conditions: [...group.conditions],
    }));

    // Add the filter condition object in the specific group
    newFilterGroups[groupIndex] = {
      ...newFilterGroups[groupIndex],
      conditions: [
        ...newFilterGroups[groupIndex].conditions,
        {
          field: null,
          operation: null,
          value: undefined,
        },
      ],
    };

    setFilterGroups(newFilterGroups);
  };

  const onRemoveFilterCondition = (
    groupIndex: number,
    conditionIndex: number,
  ) => {
    // Create a deep copy of the filterGroups state
    const newFilterGroups = filterGroups.map((group) => ({
      ...group,
      conditions: [...group.conditions],
    }));

    // If groupIndex is 0 and conditionIndex is 0 and conditions length is 1, reset the first condition
    if (
      groupIndex === 0 &&
      conditionIndex === 0 &&
      newFilterGroups.length === 1 &&
      newFilterGroups[groupIndex].conditions.length === 1
    ) {
      newFilterGroups[0] = {
        ...newFilterGroups[0],
        conditions: [
          {
            field: null,
            operation: null,
            value: undefined,
          },
        ],
      };
    } else {
      // Remove the specific condition from the group
      newFilterGroups[groupIndex] = {
        ...newFilterGroups[groupIndex],
        conditions: newFilterGroups[groupIndex].conditions.filter(
          (_, idx) => idx !== conditionIndex,
        ),
      };

      // If groupIndex > 0 and the conditions array is empty, remove the group
      if (
        (groupIndex > 0 || newFilterGroups.length > 1) &&
        newFilterGroups[groupIndex].conditions.length === 0
      ) {
        newFilterGroups.splice(groupIndex, 1);
      }
    }

    setFilterGroups(newFilterGroups);
  };

  const onUpdateFilterConditionValue = (
    value: string | number | (string | number)[],
    groupIndex: number,
    conditionIndex: number,
  ) => {
    // Create a deep copy of the filterGroups state
    const newFilterGroups = filterGroups.map((group) => ({
      ...group,
      conditions: [...group.conditions],
    }));

    // Update the specific condition value in the specific group
    newFilterGroups[groupIndex] = {
      ...newFilterGroups[groupIndex],
      conditions: newFilterGroups[groupIndex].conditions.map((condition, idx) =>
        idx === conditionIndex ? { ...condition, value } : condition,
      ),
    };

    setFilterGroups(newFilterGroups);
  };

  const onAddFilterGroup = () => {
    if (!isMaxFiltersReached) {
      setFilterGroups([
        ...filterGroups,
        getDefaultFilterGroupObject(filterGroups.length + 1),
      ]);
    }
  };

  const onSaveViewName = () => {
    if (viewNameError) return;
    setIsEditingTitle(false);
    if (editedTitle.trim() === '') {
      setEditedTitle(headerTitle);
    }
  };

  const onApplyFilters = () => {
    if (onApplyAiColEnrichmentAutoRunSettingsCondition) {
      const formatedForPayload = initialFilters
        ? null
        : formatFilterGroupsForPayload({
            groupLogic,
            filterGroups,
          });
      // Create a deep copy of filterGroups to avoid reference issues
      const deepCopyFilterGroups = filterGroups.map((group) => ({
        ...group,
        conditions: [...group.conditions],
      }));
      onApplyAiColEnrichmentAutoRunSettingsCondition(
        deepCopyFilterGroups,
        formatedForPayload,
        groupLogic,
      );
      onClose();
      return;
    }

    if (isCreateViewModal) {
      // if (
      //   filterGroups.length === 1 &&
      //   filterGroups[0].conditions.length === 1 &&
      //   filterGroups[0].conditions[0].field === null
      // ) {
      //   return;
      // }
      // Prevent multiple clicks
      if (isSaving) {
        return;
      }
      setIsSaving(true);
      // Directly save the view with default values
      onSaveAsView(
        `${editedTitle.trim()}`,
        sharedWith ===
          shareWithOptions.find((option) => option.key === 'public')?.key,
        sharedWith === 'team'
          ? shareWithTeameamIds.map((id) => Number(id))
          : [],
        'save',
      );
      return;
    }

    const payload = {
      ...filters,
      pageNum: 1,
      filterGroupLogic: initialFilters
        ? null
        : formatFilterGroupsForPayload({
            groupLogic,
            filterGroups,
          }),
      shouldFetchProspectList: true,
      shouldFetchProspectListCount: true,
      shouldFetchSavedFilters: true,
    };
    setProspectViewIdFilter(
      formatFilterGroupsForPayload({
        groupLogic,
        filterGroups,
      }),
    );
    setProspectFilters(payload);
    // removedSelectedSavedView();
    onClose();

    // Mixpanel Event
    window.analytics?.track?.({
      userId: getUserTrackingId(),
      event: AnalyticsEvents.FiltersApplied,
      properties: {
        shAccountId: getUserShAccountId(),
        role: getUserRole(),
        current_plan: getUserPlanName(),
        number_of_filters_applied: payload.filterGroupLogic?.filters
          ? payload.filterGroupLogic?.filters?.length
          : 0,
      },
    });
  };

  useEffect(() => {
    if (overlayRef?.current) {
      initialize(overlayRef.current);
    }
  }, [initialize, overlayRef?.current]);

  useEffect(() => {
    if (show) {
      if (prospectFiltersFieldsList.length === 0) {
        if (headerTitle === null) {
          sendGetProspectFiltersFieldsListRequest();
        }
      } else {
        setProspectFields(
          prospectFiltersFieldsList.map((field: ProspectFilterField) => ({
            id: field.id,
            key: field.key,
            value: field.value,
            source: field.source,
            operations: field.operations,
          })),
        );
      }
      if (
        onApplyAiColEnrichmentAutoRunSettingsCondition !== null &&
        autoRunConditionFilterGroups &&
        autoRunConditionFilterGroupsLogic
      ) {
        setFilterGroups(autoRunConditionFilterGroups);
        setGroupLogic(autoRunConditionFilterGroupsLogic);
        return;
      }
      if (prospectFiltersFieldsList?.length > 0) {
        if (selectedSavedView && selectedSavedView.filterCriteria) {
          const payload = constructFilterGroupFromPayload({
            fields: prospectFiltersFieldsList,
            filterGroups:
              prospectViewIdFilter ?? selectedSavedView.filterCriteria,
          });
          const payloadWithConditions = payload?.filter(
            (item) => item?.conditions?.length > 0,
          );
          if (
            payload?.length > 0 &&
            payloadWithConditions &&
            payloadWithConditions?.length > 0
          ) {
            setFilterGroups(payloadWithConditions);
          }
          setGroupLogic(selectedSavedView.filterCriteria.group_logic);
          return;
        }

        if (filters && filters.filterGroupLogic) {
          const payload = constructFilterGroupFromPayload({
            fields: prospectFiltersFieldsList,
            filterGroups: filters.filterGroupLogic,
          });
          setFilterGroups(payload);
          setGroupLogic(filters.filterGroupLogic.group_logic);
        }
      }
    }
  }, [
    show,
    prospectFiltersFieldsList,
    filters,
    onApplyAiColEnrichmentAutoRunSettingsCondition,
    autoRunConditionFilterGroups,
    autoRunConditionFilterGroupsLogic,
    headerTitle,
  ]);

  // When opening create-view modal, always start with a cleared set of filters.
  // This ensures that after creating a view (and closing the modal) the next
  // time the create modal is opened it does not retain previous filter state.
  useEffect(() => {
    if (show && isCreateViewModal) {
      setFilterGroups([getDefaultFilterGroupObject(1)]);
      setGroupLogic(FilterLogic.AND);
      setEditedTitle(headerTitle || '');
      setSharedWith('private');
      setShareWithTeamIds([]);
      setShowShareWithDropdown(false);
      setIsSaving(false);
    }
  }, [show, isCreateViewModal, headerTitle]);

  useEffect(() => {
    executeOnRequestStatus({
      status: getProspectFiltersFieldsListRequestStatus,
      onSuccess: () => {
        setProspectFields(
          prospectFiltersFieldsList.map((field: ProspectFilterField) => ({
            id: field.id,
            key: field.key,
            value: field.value,
            source: field.source,
            operations: field.operations,
          })),
        );
        resetGetProspectFiltersFieldsListRequest();
      },
      onFailed: () => {
        resetGetProspectFiltersFieldsListRequest();
        executeOnErrorWithErrorCheck({
          error: getProspectFiltersFieldsListRequestError,
          onError: () => {
            toaster.error(getProspectFiltersFieldsListRequestError.message);
          },
        });
      },
    });
  }, [getProspectFiltersFieldsListRequestStatus]);

  useEffect(() => {
    executeOnRequestStatus({
      status: saveProspectFiltersViewRequestStatus,
      onSuccess: () => {
        toaster.success('Successfully saved as a view.');

        resetSaveProspectFiltersViewRequest();
        hideSaveAsAViewModal();
        setIsSaving(false);
        getFilterViewListRequest();
        const { selectedFields } = filters;
        // It is done for prevent calling List api from prospect-kanban
        setShouldFetchInitialList(true);
        // Don't call list API when saving view in kanban - kanban handles its own data fetching
        if (currentViewType !== 'kanban') {
          sendGetContactListByViewIdRequest({
            viewId: selectedSavedView.id,
            selectedField: selectedFields.map((f) => f.id.toString()),
          });
        }

        if (currentViewType === 'kanban') {
          // For Getting Kanban particular lane count
          sendGetProspectListCountRequest({
            viewId: selectedSavedView.id,
            selectedField: selectedFields.map((f) => f.id.toString()),
            viewType: ViewType.Kanban,
          });
          // For Getting View Count
          sendGetProspectListCountRequest({
            viewId: selectedSavedView.id,
            // selectedField: selectedFields.map((f) => f.id.toString()),
            viewType: ViewType.List,
          });
        } else {
          sendGetProspectListCountRequest({
            viewId: selectedSavedView.id,
            selectedField: selectedFields.map((f) => f.id.toString()),
          });
        }

        setProspectFilters({
          ...filters,
          pageNum: 1,
          filterGroupLogic: formatFilterGroupsForPayload({
            groupLogic,
            filterGroups,
          }),
          shouldFetchSavedFilters: true,
        });

        // Reset all filter and form states
        setFilterGroups([getDefaultFilterGroupObject(1)]);
        setGroupLogic(FilterLogic.AND);
        setEditedTitle(headerTitle || '');
        setSharedWith('private');
        setShareWithTeamIds([]);

        onClose();

        history.push({
          search: `viewId=${selectedSavedView.id}`,
        });
      },
      onFailed: () => {
        resetSaveProspectFiltersViewRequest();
        setIsSaving(false);
        executeOnErrorWithErrorCheck({
          error: saveProspectFiltersViewRequestError,
          onError: () => {
            toaster.error(saveProspectFiltersViewRequestError.message);
          },
        });
      },
    });
  }, [saveProspectFiltersViewRequestStatus, currentViewType]);

  useEffect(() => {
    executeOnRequestStatus({
      status: updateProspectFiltersViewRequestStatus,
      onSuccess: () => {
        // toaster.success('Successfully saved as a view.');

        resetUpdateProspectFiltersViewRequest();
        hideSaveAsAViewModal();

        const { selectedFields } = filters;

        sendGetContactListByViewIdRequest({
          viewId: selectedSavedView.id,
          selectedField: selectedFields.map((f) => f.id.toString()),
        });

        sendGetProspectListCountRequest({
          viewId: selectedSavedView.id,
          selectedField: selectedFields.map((f) => f.id.toString()),
        });

        setProspectFilters({
          ...filters,
          filterGroupLogic: formatFilterGroupsForPayload({
            groupLogic,
            filterGroups,
          }),
          shouldFetchSavedFilters: true,
        });

        onClose();
      },
      onFailed: () => {
        resetUpdateProspectFiltersViewRequest();
        executeOnErrorWithErrorCheck({
          error: updateProspectFiltersViewRequestError,
          onError: () => {
            toaster.error(updateProspectFiltersViewRequestError.message);
          },
        });
      },
    });
  }, [updateProspectFiltersViewRequestStatus]);

  return (
    <Modal
      show={show}
      onHide={onClose}
      className={`prospect-filters-modal ${
        isCreateViewModal ? 'create-view-modal' : ''
      }`}
      centered={isCreateViewModal}
      enforceFocus={false}
      backdrop="static"
      aria-labelledby="contained-modal-title-vcenter"
    >
      <div className="prospect-filters-modal--container">
        <div className="prospect-filters-modal--header">
          <div className="prospect-filters-modal--header__left">
            {isEditingTitle && (
              <>
                <Input
                  type="text"
                  value={editedTitle}
                  onChange={(value: string) => {
                    setEditedTitle(value);
                    if (value.length > 128) {
                      setViewNameError(true);
                    } else {
                      setViewNameError(false);
                    }
                  }}
                  onBlur={onSaveViewName}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      onSaveViewName();
                    }
                  }}
                  autoFocus
                  className={viewNameError ? 'input-error' : ''}
                />
              </>
            )}
            {!isEditingTitle && isCreateViewModal && (
              <h1 onDoubleClick={() => setIsEditingTitle(true)}>
                <TruncatedTextWithAutoTooltip text={editedTitle} />
              </h1>
            )}
            {!isEditingTitle && !isCreateViewModal && (
              <h1>{headerTitle || 'Filters'}</h1>
            )}
            {isCreateViewModal && !isEditingTitle && (
              <OverlayTooltip text="Edit view name">
                <div
                  className="pencil-icon-container"
                  {...accessibleOnClick(() => {
                    setIsEditingTitle(true);
                  })}
                >
                  <Pencil width={16} height={16} />
                </div>
              </OverlayTooltip>
            )}
          </div>
          <div className="prospect-filters-modal--header__right">
            {!initialFilters && (
              <div className="header-button" {...accessibleOnClick(onClearAll)}>
                Clear all
              </div>
            )}
            {onApplyAiColEnrichmentAutoRunSettingsCondition === null &&
              !isCreateViewModal && (
                <div
                  className={`header-button ${hasErrors ? 'disabled' : ''} `}
                  {...accessibleOnClick(showSaveAsAViewModal)}
                >
                  <Plus />
                  <span>Save as a View</span>
                </div>
              )}
          </div>
        </div>

        {viewNameError && (
          <div className="error-text">
            View name must not be greater than 128 characters.
          </div>
        )}

        {isCreateViewModal && (
          <div className="create-view-modal-filter-title">Filter</div>
        )}

        <div className="prospect-filters-modal--content" ref={overlayRef}>
          <div className="prospect-filters-modal--group-container">
            {filterGroups.map((filterGroup, filterGroupIndex) => (
              <>
                <div
                  className="prospect-filters-modal--group"
                  key={filterGroup.group}
                >
                  {filterGroup.conditions.map((condition, conditionIndex) => {
                    const fieldsDoesNotExist = getIsFieldsDoesNotExist({
                      prospectFields,
                      field: condition?.field,
                    });

                    return (
                      <div
                        // eslint-disable-next-line react/no-array-index-key
                        key={conditionIndex}
                        className="prospect-filters-modal--condition-row"
                      >
                        <div className="condition-row-section">
                          <ProspectFiltersConditionLogic
                            groupIndex={filterGroupIndex}
                            conditionIndex={conditionIndex}
                            logic={filterGroup.logic}
                            onUpdateFilterGroupLogic={onUpdateFilterGroupLogic}
                            onApplyAiColEnrichmentAutoRunSettingsCondition={
                              onApplyAiColEnrichmentAutoRunSettingsCondition
                            }
                          />

                          <ProspectFiltersSelectDropdown
                            className="filter-fields-select"
                            selectClassName="filter-fields-dropdown"
                            placeholder="Select Field"
                            options={prospectFields}
                            optionRenderer={(option) => (
                              <div className="d-flex align-items-center justify-content-between w-100">
                                <span className="filter-field-name">
                                  {truncate(option?.value || '', 32, false)}
                                </span>
                                {fields?.find(
                                  (f) =>
                                    f?.id?.toString() ===
                                    option?.id?.toString(),
                                )?.isAiGenerated && (
                                  <ImageIcon
                                    src={Images.NoResultGradient}
                                    alt="ai-icon"
                                    width={16}
                                    height={16}
                                  />
                                )}
                              </div>
                            )}
                            selectedOptionKey={condition?.field?.key}
                            selectedOptionRenderer={(option) =>
                              fieldsDoesNotExist ? (
                                <span>
                                  {getProspectFiledLabel(condition?.field?.key)}
                                </span>
                              ) : (
                                <span>{option?.value}</span>
                              )
                            }
                            onChange={(option) =>
                              onSelectProspectFields(
                                option,
                                filterGroupIndex,
                                conditionIndex,
                              )
                            }
                            showSearch={true}
                            filterOption={(search, option) =>
                              option?.value
                                ?.toLowerCase()
                                .includes(search.toLowerCase())
                            }
                            disabled={fieldsDoesNotExist}
                          />

                          {condition.field && (
                            <ProspectFiltersOperations
                              selectedField={condition.field}
                              selectedOperation={condition.operation}
                              onSelectProspectFieldsOperation={(operation) => {
                                onSelectProspectFieldsOperation(
                                  operation,
                                  filterGroupIndex,
                                  conditionIndex,
                                );
                              }}
                              disabled={fieldsDoesNotExist}
                            />
                          )}

                          {condition.field && condition.operation && (
                            <ProspectFiltersValueComponent
                              field={condition?.field}
                              operation={condition.operation}
                              value={condition?.value}
                              onUpdateFilterConditionValue={(value) => {
                                onUpdateFilterConditionValue(
                                  value,
                                  filterGroupIndex,
                                  conditionIndex,
                                );
                              }}
                              disabled={fieldsDoesNotExist}
                              onApplyAiColEnrichmentAutoRunSettingsCondition={
                                onApplyAiColEnrichmentAutoRunSettingsCondition
                              }
                              fields={fields}
                            />
                          )}
                        </div>
                        <div
                          className="remove-condition-btn"
                          {...accessibleOnClick(() => {
                            onRemoveFilterCondition(
                              filterGroupIndex,
                              conditionIndex,
                            );
                          })}
                        >
                          <Cross />
                        </div>
                      </div>
                    );
                  })}

                  {/* Add Filter Button */}
                  <div
                    className={`add-filter-condition-btn 
                      ${
                        !isAddFilterButtonEnabled(filterGroup) ||
                        isMaxFiltersReached
                          ? 'disabled'
                          : ''
                      }
                      
                    `}
                    {...accessibleOnClick(() => {
                      if (
                        !isAddFilterButtonEnabled(filterGroup) ||
                        isMaxFiltersReached
                      ) {
                        return;
                      }

                      onAddFilterCondition(filterGroupIndex);
                    })}
                  >
                    <Plus />
                    {onApplyAiColEnrichmentAutoRunSettingsCondition ===
                      null && <span>Add Filter</span>}
                    {onApplyAiColEnrichmentAutoRunSettingsCondition !==
                      null && (
                      <span style={{ whiteSpace: 'nowrap' }}>
                        Add a Condition
                      </span>
                    )}
                  </div>
                </div>

                {filterGroups.length > 1 &&
                  filterGroupIndex < filterGroups.length - 1 && (
                    <div className="prospect-filters-modal--group-logic my-2">
                      <ProspectFiltersSelectDropdown
                        className="filter-logic-select"
                        selectClassName="filter-logic-dropdown"
                        options={[
                          {
                            key: FilterLogic.AND,
                            value: 'And',
                          },
                          {
                            key: FilterLogic.OR,
                            value: 'Or',
                          },
                        ]}
                        selectedOptionKey={groupLogic}
                        onChange={(option) => {
                          setGroupLogic(option.key);
                        }}
                        optionRenderer={(option) => <span>{option.value}</span>}
                        selectedOptionRenderer={(option) => (
                          <span>{option.value}</span>
                        )}
                      />
                    </div>
                  )}
              </>
            ))}
          </div>

          <Button
            variant="secondary"
            iconPlacement="left"
            icon={Plus}
            className={`gray-secondary-btn mt-3 ${
              isMaxFiltersReached ? 'disabled' : ''
            } ${isCreateViewModal ? 'create-view-modal-add-group-btn' : ''}`}
            onClick={onAddFilterGroup}
          >
            Add Group
          </Button>
        </div>

        {isCreateViewModal && <div className="create-view-modal-divider" />}

        <div className="prospect-filters-modal--footer">
          {isCreateViewModal && (
            <div className="prospect-filters-modal--footer__left">
              <span>Share With:</span>
              <Select
                options={shareWithOptions}
                selectedOptionKey={sharedWith}
                show={showShareWithDropdown}
                onToggle={(nextShow) => {
                  setShowShareWithDropdown(nextShow);
                  // If dropdown is being closed and user had selected 'team' but
                  // didn't pick any teams, default back to 'private'
                  if (
                    !nextShow &&
                    sharedWith === 'team' &&
                    shareWithTeameamIds.length === 0
                  ) {
                    setSharedWith('private');
                  }
                }}
                onChange={(selectedOptions) => {
                  onChangeShareWith(selectedOptions);
                }}
                optionRenderer={(option: any) => (
                  <div
                    className="d-flex align-items-center"
                    style={{ gap: '6px' }}
                  >
                    {option?.icon && <span>{option?.icon}</span>}
                    <span>{option?.label}</span>
                  </div>
                )}
                selectedOptionRenderer={(selectedOptions: any[]) => {
                  const option = selectedOptions[0];
                  if (!option) return null;

                  // show N teams selected
                  if (
                    sharedWith === 'team' &&
                    shareWithTeameamIds?.length > 0
                  ) {
                    return (
                      <div className="d-flex align-items-center">
                        <Users
                          width={16}
                          height={16}
                          className="gray-txt-15 mr-2"
                        />
                        <span>
                          {shareWithTeameamIds?.length}{' '}
                          {shareWithTeameamIds?.length === 1 ? 'Team' : 'Teams'}{' '}
                          selected
                        </span>
                      </div>
                    );
                  }

                  return (
                    <div className="d-flex align-items-center">
                      {option?.icon && <div>{option.icon}</div>}
                      <span>{option.label}</span>
                    </div>
                  );
                }}
                className="share-with-select"
                placement={Placement.Top}
                filterOption={(value, option) =>
                  option?.label?.toLowerCase().includes(value.toLowerCase())
                }
                footer={
                  sharedWith === 'team' && (
                    <Transfer
                      options={teamsList.map((team) => ({
                        key: team.id.toString(),
                        label: team.name,
                      }))}
                      isLoading={getIsRequestPending(getTeamsListRequestStatus)}
                      searchPlaceholder="Search"
                      multiSelect
                      optionRenderer={(option) => <span>{option?.label}</span>}
                      selectedOptionKey={shareWithTeameamIds?.map((id) =>
                        id.toString(),
                      )}
                      onChange={(selectedOptions) => {
                        const newTeamIds = selectedOptions.map((option) =>
                          parseInt(option.key.toString(), 10),
                        );
                        setShareWithTeamIds(newTeamIds);
                        setSharedWith('team');
                        isFiltersDirty.current = true;
                      }}
                      filterOption={(value, option) =>
                        option?.label
                          ?.toLowerCase()
                          .includes(value.toLowerCase())
                      }
                      className="share-with-team-select"
                    />
                  )
                }
              />
            </div>
          )}
          <Button
            variant="secondary"
            className={`gray-secondary-btn ${
              isCreateViewModal ? 'create-view-modal-footer-cancel-btn' : ''
            }`}
            onClick={() => {
              setViewNameError(false);
              setIsEditingTitle(false);
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={onApplyFilters}
            className={`primary-btn 
             ${isCreateViewModal ? 'create-view-modal-footer-save-btn' : ''}`}
            disabled={(initialFilters ? false : hasErrors) || viewNameError}
          >
            {isCreateViewModal ? 'Save View' : 'Apply'}
          </Button>
        </div>
      </div>

      <ProspectFiltersSaveAsAViewModal
        show={saveAsAViewModal}
        onHide={hideSaveAsAViewModal}
        onSave={onSaveAsView}
        teamsList={teamsList}
        getTeamsListRequestStatus={getTeamsListRequestStatus}
        sendGetTeamsListRequest={() => {
          sendGetTeamsListRequest();
        }}
        isLoading={
          getIsRequestPending(saveProspectFiltersViewRequestStatus) ||
          getIsRequestPending(updateProspectFiltersViewRequestStatus)
        }
        selectedSavedView={selectedSavedView}
      />
    </Modal>
  );
};

export default ProspectFiltersModal;
